/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useProperties from '../../../api/properties/useProperties';
import { useUser } from '../../../api/user/useUser';
import { ROUTE } from '../../../routes';
import { urlWithHashedId } from '../../../utils/tracking';
import {
  getAnalyticsDefaultProperties,
  getPageScrollRestorationKey,
  PAGE_SCROLL_RESTORATION_SELECTOR,
  trackPageView,
} from '@helios/frontend-core';
import LoadingPage from '../../LoadingPage/LoadingPage';
import { navConfig } from '../../Navigation/navConfig';
import { rootRoute, routes } from './Routes';

const routeTree = rootRoute.addChildren(routes);

const MAIN_PAGE_PATHS = new Set(Object.values(navConfig).map(({ path }) => path));

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: ({ location }) => MAIN_PAGE_PATHS.has(location.pathname),
  scrollToTopSelectors: [PAGE_SCROLL_RESTORATION_SELECTOR],
  getScrollRestorationKey: getPageScrollRestorationKey,
  context: {
    isAuthenticated: undefined!,
    flags: undefined!,
    property: undefined!,
    user: undefined!,
  },
  defaultPendingComponent: () => <LoadingPage />,
});

const SkipPageViewPages = new Set<ROUTE>([ROUTE.ARTICLE_WIDGETS_CLAIM_POINTS]);

const handlePageView = async (pathname: string) => {
  if (SkipPageViewPages.has(pathname as ROUTE)) return;

  const properties = await getAnalyticsDefaultProperties();
  if (!urlWithHashedId(pathname)) {
    trackPageView(properties);
  } else {
    const normalizedPath = pathname.replace(/\/[a-f0-9]{24}$/, '/:id');
    trackPageView({ path: normalizedPath, ...properties });
  }
};

router.subscribe('onLoad', (event) => {
  handlePageView(event.toLocation.pathname);
  window.parent.postMessage({ type: 'routeChange' }, '*');
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const Router: React.FC = () => {
  const { isAuthenticated, isAuthLoading, user, isLaunchDarklyIdentified } = useUser();
  const flags = useFlags();
  const propertyId = user?.propertyIds?.[0];
  const { data: property } = useProperties(propertyId);

  if (isAuthLoading || !flags || (isAuthenticated && (!user || !isLaunchDarklyIdentified))) {
    return <LoadingPage />;
  }

  return (
    <RouterProvider
      router={router}
      defaultPreload="intent"
      context={{
        isAuthenticated,
        flags,
        property,
        user,
      }}
    />
  );
};

export default Router;
